import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Music,
  Users,
  MessageCircle,
  Eye,
  EyeOff,
  Plus,
  Search,
  ArrowLeft,
  LogOut,
  Trash2,
  Camera,
  Settings,
  Share2
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useSocket } from '../hooks/useSocket';
import { useSession } from '../hooks/useSession';
import { useGeolocation } from '../hooks/useGeolocation';
import PlaylistEnhanced from './PlaylistEnhanced';
import Suggestions from './Suggestions';
import Chat from './Chat';
import Participants from './Participants';
import DirectMessage from './DirectMessage';
import DMList from './DMList';
import PhotoGallery from './PhotoGallery';
import SongSearch from './SongSearch';
import AppleMusicAuth from './AppleMusicAuth';
import AppleMusicPlayer from './AppleMusicPlayer';
import SpotifyAuth from './SpotifyAuth';
import SpotifyPlayer from './SpotifyPlayer';
import ConnectionStatus from './ConnectionStatus';
import HostSettingsPanel from './HostSettingsPanel';
import InviteModal from './InviteModal';
import RecapModal from './RecapModal';
import ActivityTicker from './ActivityTicker';
import { ReactionBar, ReactionOverlay } from './Reactions';
import { sortPlaylistByPriority, buildPlaylistArray } from '../utils/playlistScoring';
import { sessionSettings } from '../utils/roles';
import { isIOS, isInAppBrowser } from '../utils/browserEnv';
import toast from 'react-hot-toast';
import api from '../utils/api';

const Session = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user, logout, signInAsGuest, updateGuestName } = useAuth();
  const { connected, joinSession, leaveSession, toggleVisibility, totalUnreadDMs, hostAction, setActiveView, socket } = useSocket();
  const { location, getLocation, error: locationError, errorDetail: locationErrorDetail, loading: locationLoading, permissionState } = useGeolocation();

  // Get user identity - either from auth (host) or sessionStorage (guest).
  // State (not a bare read) so the QR deep-link name gate can set it.
  const [guestName, setGuestNameState] = useState(() => sessionStorage.getItem('guestName'));
  const [nameInput, setNameInput] = useState('');
  const [guestAuthFailed, setGuestAuthFailed] = useState(false);
  // PIN fallback: set when the geofence rejects the join, so the guest can
  // enter the host's party PIN instead of being bounced home
  const [joinBlocked, setJoinBlocked] = useState(null);
  const [pinInput, setPinInput] = useState('');
  const [pinJoining, setPinJoining] = useState(false);
  // Use Firebase user directly; the temporary fallback is only for display during auth
  const currentUser = user || (guestName ? {
    displayName: guestName,
    isGuest: true,
    uid: 'pending-auth'
  } : null);
  
  const [activeTab, setActiveTab] = useState('playlist');
  const [isVisible, setIsVisible] = useState(true);
  const [showSongSearch, setShowSongSearch] = useState(false);
  const [showRecap, setShowRecap] = useState(false);
  const [recapSession, setRecapSession] = useState(null);
  const [showHostSettings, setShowHostSettings] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  // Chat sub-navigation: 'group' | 'dms' | 'dm-conversation'
  const [chatMode, setChatMode] = useState('group');
  const [dmRecipient, setDmRecipient] = useState(null);
  // Guard to ensure we emit socket join only once per connection/session
  const hasSocketJoinedRef = useRef(false);
  const [joinAttempted, setJoinAttempted] = useState(false);
  const [hasJoined, setHasJoined] = useState(false);
  const [, setAppleMusicToken] = useState(null);
  const [appleMusicInstance, setAppleMusicInstance] = useState(null);
  const [spotifyConnected, setSpotifyConnected] = useState(false);

  const {
    data: session,
    isLoading,
    error,
    refetch
  } = useSession(sessionId, Boolean(user) && (hasJoined || joinAttempted));

  // Now-playing is shared session state, owned by the server and driven
  // by the host's player — not local component state
  const nowPlaying = session?.nowPlaying || null;

  // Session settings (defaults applied) drive which tabs/features exist
  const settings = sessionSettings(session);

  // Tell the socket layer which tab is visible so toasts can stay quiet
  // for content the user is already looking at
  useEffect(() => {
    setActiveView(activeTab);
  }, [activeTab, setActiveView]);

  // Democratic skip: when the crowd skips the song that's playing, the
  // host's player needs to actually move on — signal it via a counter
  const [skipSignal, setSkipSignal] = useState(0);
  const nowPlayingKeyRef = useRef(null);
  nowPlayingKeyRef.current = nowPlaying?.songId || null;
  useEffect(() => {
    if (!socket) return;
    const onSkipped = (data) => {
      if (data.songId && data.songId === nowPlayingKeyRef.current) {
        setSkipSignal((s) => s + 1);
      }
    };
    socket.on('songSkipped', onSkipped);
    return () => socket.off('songSkipped', onSkipped);
  }, [socket]);

  // If the host turns off the feature we're looking at, fall back gracefully.
  // The Chat slot holds chat when enabled, else Photos, else nothing.
  useEffect(() => {
    const chatSlotId = settings.chatEnabled ? 'chat' : settings.photosEnabled ? 'photos' : null;
    const valid = ['playlist', 'suggestions', 'participants', chatSlotId].filter(Boolean);
    if (!valid.includes(activeTab)) {
      setActiveTab('playlist');
    }
  }, [activeTab, settings.chatEnabled, settings.photosEnabled]);

  useEffect(() => {
    if (!settings.dmsEnabled && (chatMode === 'dms' || chatMode === 'dm-conversation')) {
      setChatMode('group');
      setDmRecipient(null);
      toast('The host has turned off direct messages', { icon: '🔕' });
    }
    if (!settings.photosEnabled && chatMode === 'photos') {
      setChatMode('group');
    }
  }, [settings.dmsEnabled, settings.photosEnabled, chatMode]);

  // Vote-sorted queue (played songs and the current track excluded),
  // computed regardless of active tab so the player always has it
  const sortedPlaylist = useMemo(() => {
    const playlistArray = buildPlaylistArray(session, user?.uid);
    return sortPlaylistByPriority(playlistArray, nowPlaying?.songId)
      .filter(item => !item.isPlaying);
  }, [session, user?.uid, nowPlaying?.songId]);

  // hostAction and session are recreated on every context/query render;
  // keep the latest in refs so the track-change callbacks stay stable
  // without going stale
  const hostActionRef = useRef(hostAction);
  hostActionRef.current = hostAction;
  const sessionRef = useRef(session);
  sessionRef.current = session;

  // Host's player reports track changes; the server marks the previous
  // song played and broadcasts the new now-playing to everyone
  const handleTrackChange = useCallback((playlistKey) => {
    hostActionRef.current('updateNowPlaying', null, playlistKey);
  }, []);

  // Spotify reports raw tracks — resolve them back to a playlist key
  const handleSpotifyTrackChange = useCallback((track) => {
    if (!track?.id) return;
    const playlist = sessionRef.current?.playlist || {};
    const match = Object.entries(playlist).find(([key, s]) =>
      key === track.id || s.id === track.id || s.originalId === track.id
    );
    if (match) {
      hostActionRef.current('updateNowPlaying', null, match[0]);
    }
  }, []);

  // Check for existing Apple Music token
  useEffect(() => {
    const savedToken = localStorage.getItem('appleMusicToken');
    if (savedToken) {
      setAppleMusicToken(savedToken);
    }
  }, []);

  // Ensure anonymous auth for guests so we have a token for API joins
  useEffect(() => {
    if (!user && guestName && !guestAuthFailed) {
      signInAsGuest(guestName).catch((err) => {
        console.error('Guest auth failed:', err);
        setGuestAuthFailed(true);
        toast.error('Failed to connect. Please go back and try again.');
      });
    }
  }, [user, guestName, signInAsGuest, guestAuthFailed]);

  // Firebase persists anonymous users across visits, so a returning guest
  // keeps their previous name even after typing a new one. If the entered
  // name differs from the persisted profile, rename before joining.
  const needsGuestRename = Boolean(user?.isGuest && guestName && user.displayName !== guestName);
  const renameInFlightRef = useRef(false);
  useEffect(() => {
    if (needsGuestRename && !renameInFlightRef.current) {
      renameInFlightRef.current = true;
      updateGuestName(guestName)
        .catch((err) => console.error('Guest rename failed:', err))
        .finally(() => {
          renameInFlightRef.current = false;
        });
    }
  }, [needsGuestRename, guestName, updateGuestName]);

  // Reset join guard when connection drops or session changes
  useEffect(() => {
    if (!connected) {
      hasSocketJoinedRef.current = false;
    }
  }, [connected]);

  useEffect(() => {
    hasSocketJoinedRef.current = false;
  }, [sessionId]);

  // Join socket room ONLY after successful API session join
  useEffect(() => {
    if (connected && sessionId && hasJoined && !hasSocketJoinedRef.current) {
      console.log('[Session] Joining socket room after API session join success');
      joinSession(sessionId);
      hasSocketJoinedRef.current = true;
    }
  }, [connected, sessionId, hasJoined, joinSession]);

  // Get location on mount
  useEffect(() => {
    getLocation();
  }, [getLocation]);

  // Handle session join
  // Try joining via API as soon as we have location and a user token —
  // but wait for any pending guest rename so the token carries the right name
  useEffect(() => {
    if (!joinAttempted && location && user && !needsGuestRename) {
      setJoinAttempted(true);
      handleJoinSession();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, user, joinAttempted, needsGuestRename]);

  const handleJoinSession = async () => {
    if (!location || !currentUser) return;

    try {
      // Join session via API (idempotent on server side)
      const response = await api.post(`/api/sessions/${sessionId}/join`, {
        location: {
          latitude: location.latitude,
          longitude: location.longitude
        }
      });

      if (response?.status >= 200 && response?.status < 300) {
        toast.success('Joined session successfully!');
        setHasJoined(true);
      }
      refetch();
    } catch (error) {
      console.error('Join session error:', error);
      const serverMsg = error.response?.data?.error || '';
      if (serverMsg.toLowerCase().includes('geofence')) {
        // Outside the party area — offer the PIN path instead of bouncing home
        setJoinBlocked('Your phone\'s location puts you outside the party area');
        return;
      }
      toast.error(serverMsg || error.message || 'Failed to join session');
      navigate('/');
    }
  };

  // Join using the host's 4-digit party PIN (location optional)
  const handleJoinWithPin = async (e) => {
    e.preventDefault();
    const pin = pinInput.trim();
    if (!/^\d{4}$/.test(pin)) {
      toast.error('The PIN is 4 digits');
      return;
    }
    setPinJoining(true);
    try {
      const body = { pin };
      if (location) {
        body.location = { latitude: location.latitude, longitude: location.longitude };
      }
      const response = await api.post(`/api/sessions/${sessionId}/join`, body);
      if (response?.status >= 200 && response?.status < 300) {
        toast.success('Joined session successfully!');
        setJoinBlocked(null);
        setHasJoined(true);
        refetch();
      }
    } catch (error) {
      console.error('PIN join error:', error);
      toast.error(error.response?.data?.error || 'Could not join with that PIN');
    } finally {
      setPinJoining(false);
    }
  };

  // Host ends the party: session closes for everyone, the recap pops,
  // and the night lands on the host's permanent record
  const handleEndParty = async () => {
    if (!window.confirm('End the party for everyone? The recap comes up next.')) return;
    try {
      // Freeze the session snapshot BEFORE the server clears it
      setRecapSession(session);
      await api.post(`/api/sessions/${sessionId}/end`);
      leaveSession();
      setShowRecap(true);
    } catch (error) {
      console.error('End party error:', error);
      setRecapSession(null);
      toast.error(error.response?.data?.error || 'Could not end the party');
    }
  };

  const handleLeaveSession = async () => {
    try {
      leaveSession();
      
      const response = await api.post(`/api/sessions/${sessionId}/leave`);

      if (response?.status >= 200 && response?.status < 300) {
        toast.success('Left session successfully');
        navigate('/');
      }
    } catch (error) {
      console.error('Leave session error:', error);
      toast.error('Failed to leave session');
    }
  };

  const handleDeleteSession = async () => {
    try {
      const resp = await api.delete(`/api/sessions/${sessionId}`);
      if (resp?.status >= 200 && resp?.status < 300) {
        toast.success('Session deleted');
        navigate('/');
      }
    } catch (error) {
      console.error('Delete session error:', error);
      toast.error('Failed to delete session');
    }
  };

  const handleToggleVisibility = () => {
    const newVisibility = !isVisible;
    setIsVisible(newVisibility);
    toggleVisibility(newVisibility);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to logout');
    }
  };

  // Navigate to DM with a specific user (from Participants tab or DMList)
  const handleMessageUser = (recipient) => {
    setDmRecipient(recipient);
    setChatMode('dm-conversation');
    setActiveTab('chat');
  };

  // QR deep-link entry: a guest who scanned a code has no name yet —
  // ask for one right here instead of leaving them on a dead screen
  if (!user && !guestName) {
    const submitName = (e) => {
      e.preventDefault();
      const name = nameInput.trim();
      if (name.length < 2) {
        toast.error('Please enter at least 2 characters');
        return;
      }
      sessionStorage.setItem('guestName', name);
      setGuestNameState(name);
    };

    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="card max-w-sm w-full text-center">
          <Music className="w-12 h-12 text-purple-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-white mb-2">Join the session</h1>
          <p className="text-gray-400 text-sm mb-6">
            What should we call you? Other guests will see this name.
          </p>
          <form onSubmit={submitName} className="space-y-3">
            <input
              type="text"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              placeholder="Your name"
              className="input-field w-full text-center"
              maxLength={30}
              autoFocus
            />
            <button
              type="submit"
              disabled={nameInput.trim().length < 2}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Join Session
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Location failed (or the geofence rejected us) and we haven't joined:
  // be honest about it and offer the host's party PIN as the way in. The
  // most common quirk: the browser remembered an earlier "deny" and will
  // never show the prompt again — Try Again can't fix that, only settings.
  if (!hasJoined && ((!location && locationError) || joinBlocked)) {
    const permanentlyBlocked = permissionState === 'denied';
    const inApp = isInAppBrowser();
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="card max-w-sm w-full text-center">
          <Users className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-white mb-2">
            {joinBlocked ? 'Almost there' : 'We need your location'}
          </h1>
          <p className="text-gray-400 text-sm mb-4">
            {joinBlocked
              ? `${joinBlocked}. If you're really here, ask the host for the party PIN.`
              : 'Sessions are location-based — we check once that you\'re at the party.'}
          </p>

          {/* The escape hatch: the host's 4-digit party PIN */}
          <form onSubmit={handleJoinWithPin} className="bg-gray-700/60 rounded-lg p-3 mb-4">
            <p className="text-sm text-gray-300 mb-2 font-medium">
              Have the party PIN from the host?
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
                placeholder="4-digit PIN"
                aria-label="Party PIN"
                className="input-field flex-1 text-center font-mono tracking-widest"
              />
              <button
                type="submit"
                disabled={pinJoining || pinInput.length !== 4}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {pinJoining ? '…' : 'Join'}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              The host can find it under the Share button
            </p>
          </form>

          {joinBlocked ? null : inApp ? (
            <div className="bg-yellow-600/15 border border-yellow-500/30 rounded-lg p-3 mb-4 text-left">
              <p className="text-yellow-300 text-sm font-medium mb-1">
                You're in an app's built-in browser
              </p>
              <p className="text-gray-300 text-xs">
                It usually can't ask for location. Tap the ··· or share menu and
                choose <span className="text-white">"Open in Safari"</span> or{' '}
                <span className="text-white">"Open in Chrome"</span>, then try again.
              </p>
            </div>
          ) : permanentlyBlocked ? (
            <div className="bg-yellow-600/15 border border-yellow-500/30 rounded-lg p-3 mb-4 text-left">
              <p className="text-yellow-300 text-sm font-medium mb-1">
                Location is blocked for this site
              </p>
              {isIOS() ? (
                <ol className="text-gray-300 text-xs space-y-1 list-decimal list-inside">
                  <li>In Safari, tap the <span className="text-white font-mono">ᴀA</span> (or puzzle) icon in the address bar</li>
                  <li>Tap <span className="text-white">Website Settings → Location → Allow</span></li>
                  <li>Also check <span className="text-white">Settings → Privacy & Security → Location Services → Safari Websites → While Using</span></li>
                  <li>Come back and tap Try Again</li>
                </ol>
              ) : (
                <ol className="text-gray-300 text-xs space-y-1 list-decimal list-inside">
                  <li>Tap the <span className="text-white">lock icon</span> next to the web address</li>
                  <li>Tap <span className="text-white">Permissions → Location → Allow</span></li>
                  <li>Come back and tap Try Again</li>
                </ol>
              )}
            </div>
          ) : (
            <p className="text-gray-400 text-sm mb-4">{locationError}. Tap below and
              choose <span className="text-white">Allow</span> when your browser asks.</p>
          )}

          {!joinBlocked && locationErrorDetail && (
            <p className="text-gray-400 text-xs mb-3 font-mono">
              Technical detail: {locationErrorDetail}
            </p>
          )}

          <div className="space-y-3">
            {!joinBlocked && (
              <button
                onClick={getLocation}
                disabled={locationLoading}
                className="btn-secondary w-full disabled:opacity-50"
              >
                {locationLoading ? 'Locating…' : 'Retry Location'}
              </button>
            )}
            <button onClick={() => navigate('/')} className="btn-secondary w-full">
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">Failed to load session</p>
          <p className="text-gray-400 text-sm mb-4">It may have ended or the link may be wrong.</p>
          <button
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (isLoading || !session) {
    // Covers the whole pre-session pipeline: locating, signing in, joining, fetching
    const stage = !location
      ? 'Getting your location…'
      : !user
        ? 'Signing you in…'
        : 'Loading session…';
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-400">{stage}</p>
        </div>
      </div>
    );
  }

  const participant = session.participants?.[currentUser?.uid];
  const isHost = participant?.isHost;

  // Tab model: Photos folds into the Chat tab as a third sub-view when both
  // are enabled; a standalone Photos tab appears only when chat is off.
  const chatOrPhotosTab = settings.chatEnabled
    ? { id: 'chat', label: 'Chat', icon: MessageCircle }
    : settings.photosEnabled
      ? { id: 'photos', label: 'Photos', icon: Camera }
      : null;
  const tabs = [
    { id: 'playlist', label: 'Queue', icon: Music },
    { id: 'suggestions', label: 'Suggest', icon: Plus },
    ...(chatOrPhotosTab ? [chatOrPhotosTab] : []),
    { id: 'participants', label: 'People', icon: Users }
  ];

  const renderTabButton = (tab, mobile) => {
    const Icon = tab.icon;
    return (
      <button
        key={tab.id}
        onClick={() => setActiveTab(tab.id)}
        className={
          mobile
            ? `relative flex flex-col items-center justify-center gap-0.5 flex-1 py-2 transition-colors ${
                activeTab === tab.id ? 'text-white' : 'text-gray-400'
              }`
            : `relative flex items-center justify-center gap-2 px-4 py-2 rounded-md transition-colors flex-1 min-w-0 ${
                activeTab === tab.id
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`
        }
      >
        <Icon className={`w-5 h-5 shrink-0 ${mobile && activeTab === tab.id ? 'jh-gradient-text' : ''}`} />
        <span className={mobile ? 'text-[10px] leading-tight' : 'text-sm'}>{tab.label}</span>
        {tab.id === 'chat' && totalUnreadDMs > 0 && (
          <span className="absolute -top-1 right-1/4 sm:-right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {totalUnreadDMs > 9 ? '9+' : totalUnreadDMs}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center space-x-4 min-w-0">
              <button
                onClick={() => navigate('/')}
                className="text-gray-400 hover:text-white transition-colors flex-shrink-0"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>

              <div className="min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <h1 className="text-lg sm:text-xl font-semibold text-white truncate">{session.name}</h1>
                  {session.status === 'active' && (
                    <span className="flex items-center gap-1 text-[10px] font-bold text-green-400 bg-green-500/10 border border-green-500/30 rounded-full px-2 py-0.5 shrink-0">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                      LIVE
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-400">
                  <span className="flex items-center">
                    <Users className="w-4 h-4 mr-1" />
                    {Object.keys(session.participants || {}).length}
                  </span>
                  <span className="flex items-center">
                    <Music className="w-4 h-4 mr-1" />
                    {Object.keys(session.playlist || {}).length}
                  </span>
                  {isHost && (
                    <span className="bg-purple-600 text-white px-2 py-0.5 rounded text-xs">
                      Host
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
              {/* Invite guests — everyone can grow the party */}
              <button
                onClick={() => setShowInvite(true)}
                className="p-2 rounded-lg bg-gray-600 text-gray-300 hover:text-white transition-colors"
                title="Invite guests"
                aria-label="Invite guests"
              >
                <Share2 className="w-5 h-5" />
              </button>

              {/* Host Session Controls */}
              {isHost && (
                <button
                  onClick={() => setShowHostSettings(true)}
                  className="p-2 rounded-lg bg-gray-600 text-gray-300 hover:text-white transition-colors"
                  title="Session controls"
                >
                  <Settings className="w-5 h-5" />
                </button>
              )}

              {/* Visibility Toggle */}
              <button
                onClick={handleToggleVisibility}
                className={`p-2 rounded-lg transition-colors ${
                  isVisible 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-600 text-gray-300 hover:text-white'
                }`}
                title={isVisible ? 'Visible to others' : 'Invisible to others'}
              >
                {isVisible ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
              </button>

              {/* Enhanced Connection Status */}
              <ConnectionStatus />

              {/* Logout */}
              <button
                onClick={handleLogout}
                className="text-gray-400 hover:text-white transition-colors"
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
                {isHost && (
                  <button
                    onClick={handleDeleteSession}
                    className="text-red-400 hover:text-red-300 transition-colors p-2 rounded-lg"
                    title="Delete Session"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
            </div>
          </div>
        </div>
      </div>

      {/* Live party moments */}
      <ActivityTicker />

      {/* Floating reactions from everyone, visible on any tab */}
      <ReactionOverlay />

      {/* Main Content — extra bottom padding clears the mobile bottom nav */}
      <div className="container mx-auto px-4 pb-28 pt-2 md:pb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {/* Main Content Area */}
          <div className="md:col-span-2 lg:col-span-3">
            {/* Tabs — desktop only; mobile uses the bottom nav */}
            <div className="hidden md:flex mb-6 bg-gray-800 rounded-lg p-1 overflow-x-auto whitespace-nowrap">
              {tabs.map((tab) => renderTabButton(tab, false))}
            </div>

            {/* Tab Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
              >
                {activeTab === 'playlist' && (
                  <>
                    <PlaylistEnhanced
                      session={session}
                      currentPlayingSongId={nowPlaying?.songId}
                    />
                    <ReactionBar className="mt-4" />
                  </>
                )}
                {activeTab === 'suggestions' && (
                  <Suggestions 
                    session={session} 
                    onShowSearch={() => setShowSongSearch(true)}
                  />
                )}
                {activeTab === 'chat' && (
                  <div>
                    {/* Group / DMs / Photos toggle - only show when not in a conversation */}
                    {chatMode !== 'dm-conversation' && (settings.dmsEnabled || settings.photosEnabled) && (
                      <div className="flex mb-4 bg-gray-700 rounded-lg p-1">
                        <button
                          onClick={() => setChatMode('group')}
                          className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            chatMode === 'group'
                              ? 'bg-purple-600 text-white'
                              : 'text-gray-400 hover:text-white'
                          }`}
                        >
                          Group
                        </button>
                        {settings.dmsEnabled && (
                          <button
                            onClick={() => setChatMode('dms')}
                            className={`relative flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                              chatMode === 'dms'
                                ? 'bg-indigo-600 text-white'
                                : 'text-gray-400 hover:text-white'
                            }`}
                          >
                            DMs
                            {totalUnreadDMs > 0 && (
                              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                                {totalUnreadDMs > 9 ? '9+' : totalUnreadDMs}
                              </span>
                            )}
                          </button>
                        )}
                        {settings.photosEnabled && (
                          <button
                            onClick={() => setChatMode('photos')}
                            className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                              chatMode === 'photos'
                                ? 'bg-purple-600 text-white'
                                : 'text-gray-400 hover:text-white'
                            }`}
                          >
                            Photos
                          </button>
                        )}
                      </div>
                    )}

                    {chatMode === 'group' && <Chat session={session} />}
                    {chatMode === 'photos' && <PhotoGallery />}
                    {chatMode === 'dms' && (
                      <DMList
                        session={session}
                        onOpenConversation={(recipient) => {
                          setDmRecipient(recipient);
                          setChatMode('dm-conversation');
                        }}
                      />
                    )}
                    {chatMode === 'dm-conversation' && dmRecipient && (
                      <DirectMessage
                        recipient={dmRecipient}
                        session={session}
                        onBack={() => {
                          setChatMode('dms');
                          setDmRecipient(null);
                        }}
                      />
                    )}
                  </div>
                )}
                {activeTab === 'photos' && (
                  <PhotoGallery />
                )}
                {activeTab === 'participants' && (
                  <Participants
                    session={session}
                    onMessageUser={handleMessageUser}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Sidebar */}
          <div className="md:col-span-1 lg:col-span-1">
            <div className="space-y-6">
              {/* Quick Actions */}
              <div className="card">
                <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <button
                    onClick={() => setShowSongSearch(true)}
                    className="w-full btn-primary flex items-center justify-center"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    Suggest Song
                  </button>
                  
                  {isHost && (
                    <button
                      onClick={handleEndParty}
                      className="w-full py-2.5 rounded-lg bg-red-600/20 text-red-300 font-medium hover:bg-red-600/30 transition-colors"
                    >
                      End Party
                    </button>
                  )}
                  <button
                    onClick={handleLeaveSession}
                    className="w-full btn-secondary"
                  >
                    Leave Session
                  </button>
                </div>
              </div>

              {/* Apple Music (Host Only) */}
              {session.musicService === 'apple' && isHost && (
                <div className="space-y-4">
                  <AppleMusicAuth 
                    onAuthenticated={(token) => {
                      console.log('[Session] Apple Music authenticated with token');
                      setAppleMusicToken(token);
                      localStorage.setItem('appleMusicToken', token);
                      
                      // Get MusicKit instance for playback
                      if (window.MusicKit) {
                        const musicInstance = window.MusicKit.getInstance();
                        setAppleMusicInstance(musicInstance);
                        console.log('[Session] Apple Music instance ready for playback');
                      }
                    }}
                  />
                  
                  {/* Apple Music Player */}
                  {appleMusicInstance && (
                    <AppleMusicPlayer
                      musicInstance={appleMusicInstance}
                      upNext={sortedPlaylist}
                      nowPlaying={nowPlaying}
                      onTrackChange={handleTrackChange}
                      skipSignal={skipSignal}
                    />
                  )}
                </div>
              )}

              {/* Guest Info for Apple Music Sessions */}
              {session.musicService === 'apple' && !isHost && (
                <div className="card bg-pink-600/10 border-pink-500/30">
                  <div className="flex items-center space-x-3 mb-3">
                    <Music className="w-5 h-5 text-pink-400" />
                    <h3 className="text-lg font-semibold">Apple Music Session</h3>
                  </div>
                  <p className="text-sm text-gray-300">
                    You can search and suggest Apple Music songs. The host will play them through their Apple Music account.
                  </p>
                </div>
              )}

              {/* Spotify (Host Only) */}
              {session.musicService === 'spotify' && isHost && (
                <div className="space-y-4">
                  <SpotifyAuth
                    onAuthenticated={() => {
                      console.log('[Session] Spotify authenticated');
                      setSpotifyConnected(true);
                    }}
                  />

                  {spotifyConnected && (
                    <SpotifyPlayer
                      sessionPlaylist={sortedPlaylist}
                      onSongChange={handleSpotifyTrackChange}
                    />
                  )}
                </div>
              )}

              {/* Guest Info for Spotify Sessions */}
              {session.musicService === 'spotify' && !isHost && (
                <div className="card bg-green-600/10 border-green-500/30">
                  <div className="flex items-center space-x-3 mb-3">
                    <Music className="w-5 h-5 text-green-400" />
                    <h3 className="text-lg font-semibold">Spotify Session</h3>
                  </div>
                  <p className="text-sm text-gray-300">
                    You can search and suggest Spotify songs. The host will play them through their Spotify account.
                  </p>
                </div>
              )}

              {/* Session Info */}
              <div className="card">
                <h3 className="text-lg font-semibold mb-4">Session Info</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Status:</span>
                    <span className="text-green-400">Active</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Music Service:</span>
                    <span className="capitalize">{session.musicService}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Created:</span>
                    <span>{new Date(session.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile bottom navigation — tabs around a raised gradient FAB */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 jh-glass pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-stretch">
          {tabs.slice(0, 2).map((tab) => renderTabButton(tab, true))}
          <div className="flex-1 flex items-center justify-center relative">
            <button
              onClick={() => setShowSongSearch(true)}
              className="jh-gradient text-white rounded-full p-4 shadow-lg shadow-purple-600/40 -translate-y-4 active:scale-95 transition-transform"
              aria-label="Add a song"
            >
              <Plus className="w-6 h-6" />
            </button>
          </div>
          {tabs.slice(2).map((tab) => renderTabButton(tab, true))}
        </div>
      </nav>

      {/* Song Search Modal */}
      {showRecap && recapSession && (
        <RecapModal
          session={recapSession}
          onClose={() => {
            setShowRecap(false);
            navigate('/');
          }}
        />
      )}

      {showSongSearch && (
        <SongSearch
          session={session}
          onClose={() => setShowSongSearch(false)}
        />
      )}

      {/* Host Session Controls */}
      {showHostSettings && isHost && (
        <HostSettingsPanel
          session={session}
          onClose={() => setShowHostSettings(false)}
        />
      )}

      {/* Invite Modal */}
      {showInvite && (
        <InviteModal
          session={session}
          onClose={() => setShowInvite(false)}
        />
      )}
    </div>
  );
};

export default Session; 